/* eslint-disable max-lines */
import Textarea from 'components/Input/Textarea';
import Modal from 'components/Modal';
import Table, { OriginalRow } from 'components/Table';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import actions from 'state/actions';
import styled from 'styled-components/macro';
import utils from 'utils';
import { useAsync } from 'react-use';
import selectors from 'state/selectors';
import service from 'services';
import { Group } from 'models/response';
import { moduleProgramCode } from 'utils/constants';
import { useDocumentData } from 'hooks/useDocumentData';
import { useTableColumns } from 'hooks/useTableColumns';
import { DocumentTables } from 'components/DocumentTables/DocumentTables';
import { WorkflowLogRow } from '../../workflowLog/workflowLog';

const Label = styled.p`
  font-size: ${({ theme }) => theme.fontSizePalette.medium};
  font-weight: ${({ theme }) => theme.fontWeightPalette.light};
  color: ${({ theme }) => theme.colorPalette.grey.grey9};
  margin-bottom: 10px;
`;

const NoteMandatoryLabel = styled.span`
  color: ${({ theme }) => theme.colorPalette.grey.grey9};
  font-size: ${({ theme }) => theme.fontSizePalette.xxSmall};
`;

const Log = () => {
  const [note, setNote] = useState('');
  const [selectedRow, setSelectedRow] = useState<WorkflowLogRow | null>(null);
  const dispatch = useDispatch();
  const t = utils.intl.useTranslator();

  const { tableRows, historyRows, selectedGroup, setSelectedGroup, tasks, fetchData, activeDocument } =
    useDocumentData();

  const { tableColumns, historyColumns } = useTableColumns();

  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const idUser = useSelector(selectors.user.getIdUser);
  const idProgram = useSelector(selectors.app.getProgramCode) || -1;
  const activeTemplateId = useSelector(selectors.documents.selectActiveTemplateId);
  const notify = utils.app.notify;

  const channel = activeDocument?.incChannel ?? activeDocument?.channel;
  const isInWorkFlow =
    idProgram === moduleProgramCode.invoiceWorkflow || idProgram === moduleProgramCode.invoiceInteractiveExport;
  const isInWorkFlowWithTemplate =
    idProgram === moduleProgramCode.invoiceWorkflow ||
    (idProgram === moduleProgramCode.invoiceInteractiveExport && activeTemplateId === 5);

  useAsync(async () => {
    await fetchData(channel);
  }, []);

  const onConfirm = async () => {
    try {
      if (protocol && idUser) {
        await service.postWfHistoryNote(protocol, idUser, note);
        dispatch(actions.modal.closeModal());
        isInWorkFlowWithTemplate && utils.app.notify('success', t('note-inserted'));
      }
    } catch (e) {
      console.error(e);
      notify('fail', t(e as string));
    }
  };

  const handleSelection = (item: any[]) => {
    setSelectedRow(item.length ? item[0] : null);
    if (!item.length) setSelectedGroup(null);
  };

  const handleGroupSelection = (item: Group[]) => {
    setSelectedGroup(item?.length ? item[0] : null);
  };

  return (
    <>
      <Modal.Header title={t('Document Log')} subtitle={t('document-log-subtitle')} />
      <Modal.Content>
        <Label>{t('Document history')}</Label>
        <Table
          id="document-log-table"
          sortBy={[{ id: 'dateAndTime', desc: true }]}
          columns={tableColumns}
          rows={tableRows}
          hasPagination
          hasSort
          hasFilter
          hasSelection
          onSelection={handleSelection}
        />

        <DocumentTables
          tasks={tasks}
          selectedDocument={selectedRow}
          selectedGroup={selectedGroup}
          onGroupSelection={handleGroupSelection}
        />

        {historyRows !== null && isInWorkFlow && (
          <>
            <Label>{channel === 1 ? t('mail-history') : channel === 3 ? t('pdf-history') : t('disk-history')}</Label>
            <Table
              id="document-history-table"
              sortBy={[{ id: 'insDate', desc: true }]}
              columns={historyColumns}
              rows={historyRows as OriginalRow[]}
              hasPagination
              hasSort
              hasFilter
              hasToolbar
            />
          </>
        )}
        {isInWorkFlowWithTemplate && (
          <>
            <Label>{t('Log notes')}</Label>
            <Textarea minHeight="50px" height="71px" onChange={(e) => setNote(e.target.value)} />
            <NoteMandatoryLabel>{t('note-mandatory1')}</NoteMandatoryLabel>
          </>
        )}
      </Modal.Content>

      {isInWorkFlowWithTemplate ? (
        <Modal.Footer confirmAction={onConfirm} confirmText={t('save-log-notes')} confirmDisabled={note.length === 0} />
      ) : (
        <Modal.Footer withCloseButton closeButtonText={t('close')} />
      )}
    </>
  );
};

export default Log;
